import React, { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Box, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import {
  fetchOnboardingStatus,
  onboardingFetchedSelector,
  onboardingLoadingSelector,
  onboardingSelector
} from '../redux/features/candidate/candidateSlice';
import { APP_PATH_ONBOARDING } from '../config';

const useStyles = makeStyles(() => ({
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh'
  }
}));

/**
 * OnboardingGuard component that checks if a candidate has completed onboarding.
 * If not completed, redirects to the onboarding page.
 */
const OnboardingGuard = ({ children, isCandidate }) => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const onboarding = useSelector(onboardingSelector);
  const onboardingLoading = useSelector(onboardingLoadingSelector);
  const onboardingFetched = useSelector(onboardingFetchedSelector);

  useEffect(() => {
    // Only check onboarding for candidates
    if (!isCandidate) {
      return;
    }

    // Don't check if already on onboarding page
    if (location.pathname === APP_PATH_ONBOARDING) {
      return;
    }

    // Only fetch once (check Redux state)
    if (onboardingFetched) {
      return;
    }

    // Fetch onboarding status
    console.log('🔒 [OnboardingGuard] Checking onboarding status');
    dispatch(fetchOnboardingStatus());
  }, [dispatch, isCandidate, location.pathname, onboardingFetched]);

  // Redirect to onboarding if needed (based on Redux state)
  useEffect(() => {
    if (
      !isCandidate ||
      !onboarding ||
      location.pathname === APP_PATH_ONBOARDING
    ) {
      return;
    }

    const { status } = onboarding;
    console.log('🔒 [OnboardingGuard] Current status:', status);

    // Redirect to onboarding if not completed or skipped
    if (status === 'NOT_STARTED' || status === 'IN_PROGRESS') {
      console.log('🔒 [OnboardingGuard] Redirecting to onboarding');
      history.push(APP_PATH_ONBOARDING);
    }
  }, [isCandidate, onboarding, location.pathname, history]);

  // Show loading while checking onboarding status
  if (isCandidate && onboardingLoading && !onboarding) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  // If on onboarding page, always allow
  if (location.pathname === APP_PATH_ONBOARDING) {
    return children;
  }

  // If onboarding exists and is not completed, show loading (will redirect via useEffect)
  if (
    isCandidate &&
    onboarding &&
    (onboarding.status === 'NOT_STARTED' || onboarding.status === 'IN_PROGRESS')
  ) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  // Otherwise, render children
  return children;
};

OnboardingGuard.propTypes = {
  children: PropTypes.node,
  isCandidate: PropTypes.bool
};

export default OnboardingGuard;
